/**
 * Carrossel genérico baseado em scroll-snap.
 *
 * Marcação esperada:
 *   <div data-carousel data-autoplay="5000">
 *     <ul data-carousel-track> <li> ... </li> </ul>
 *     <button data-carousel-prev></button> <button data-carousel-next></button>
 *     <div data-carousel-dots></div>            (opcional, os pontos são gerados aqui)
 *     <button data-carousel-thumb></button> ... (opcional, miniaturas na ordem dos slides)
 *
 * Sem JavaScript o carrossel continua utilizável por rolagem/arraste.
 *
 * Conteúdo carregado depois (ex.: produtos relacionados buscados por fetch) não é pego
 * automaticamente porque a varredura abaixo só roda uma vez. Nesses casos, o script que injeta
 * o conteúdo novo deve chamar window.themeCarousel.init(elementoOuLista).
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initCarousel = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    const slides = Array.from(track.children);
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const dotsWrap = root.querySelector('[data-carousel-dots]');
    const thumbs = Array.from(root.querySelectorAll('[data-carousel-thumb]'));
    const label = (key, index) => (root.dataset[key] || '').replace('__INDEX__', index);

    let dots = [];
    let timer = null;

    const maxScroll = () => track.scrollWidth - track.clientWidth;

    const currentIndex = () => {
      const left = track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - track.offsetLeft - left);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      return left >= maxScroll() - 2 ? slides.length - 1 : closest;
    };

    const goTo = (index, behavior = 'smooth') => {
      const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : behavior });
    };

    const step = (direction) => {
      const amount = track.clientWidth * 0.9 * direction;
      track.scrollBy({ left: amount, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    // Roda a cada quadro enquanto a galeria desliza: lê tudo primeiro e só mexe no DOM quando o
    // estado muda (antes reescrevia aria-current de todos os pontos e miniaturas a cada quadro).
    let last = {};
    const update = () => {
      const max = maxScroll();
      const left = track.scrollLeft;
      const state = {
        scrollable: max > 2,
        index: currentIndex(),
        atStart: left <= 2,
        atEnd: left >= max - 2,
      };
      // Tudo cabe na tela: setas e pontinhos não têm função, então somem
      if (state.scrollable !== last.scrollable) {
        root.classList.toggle('carousel--static', !state.scrollable);
        if (dotsWrap) dotsWrap.hidden = !state.scrollable;
      }
      if (prev && state.atStart !== last.atStart) prev.disabled = state.atStart;
      if (next && state.atEnd !== last.atEnd) next.disabled = state.atEnd;
      if (state.index !== last.index) {
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === state.index)));
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === state.index)));
      }
      last = state;
    };

    if (dotsWrap && slides.length > 1) {
      dots = slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel__dot';
        dot.setAttribute('aria-label', label('labelGo', i + 1));
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.append(dot);
        return dot;
      });
    }

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      },
      { passive: true },
    );
    window.addEventListener('resize', update);

    // Reprodução automática (pausa ao interagir e quando o usuário prefere menos movimento)
    const delay = Number(root.dataset.autoplay);
    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };
    // Fora da tela ou com a aba escondida o giro automático para: não desliza o que ninguém vê
    let inView = true;
    const start = () => {
      if (!delay || reducedMotion.matches || slides.length < 2 || timer || !inView || document.hidden) return;
      timer = window.setInterval(() => {
        const index = currentIndex();
        goTo(index >= slides.length - 1 ? 0 : index + 1);
      }, delay);
    };
    if (delay) {
      ['mouseenter', 'focusin', 'touchstart'].forEach((type) => root.addEventListener(type, stop, { passive: true }));
      ['mouseleave', 'focusout'].forEach((type) => root.addEventListener(type, start));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          inView = entry.isIntersecting;
          if (inView) start();
          else stop();
        }).observe(root);
      }
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
      start();
    }

    // Editor de temas: ao selecionar um bloco, mostra o slide correspondente
    root.addEventListener('shopify:block:select', (event) => {
      stop();
      const index = slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
      if (index >= 0) goTo(index, 'auto');
    });
    root.addEventListener('shopify:block:deselect', start);

    update();
  };

  const init = (target) => {
    const elements = target instanceof Element ? [target] : Array.from(target || []);
    elements.forEach(initCarousel);
  };

  document.querySelectorAll('[data-carousel]').forEach(initCarousel);
  // Editor de temas: a seção redesenhada volta sem os eventos, então inicia de novo
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-carousel]').forEach(initCarousel));
  window.themeCarousel = { init };
})();
